// stores/eventStore.ts
import { defineStore } from 'pinia';
import type { AvatarConfig } from 'src/modules/Avatar/types/avatarConfig';

// Define your event types and their payloads
export interface EventMap {
  'action:instruction': { metadata?: string };
  'action:help': { metadata?: string };
  'action:interim': { metadata?: string };
  'action:reset': { metadata?: string };
  'action:toggle-drawer': { metadata?: string };
  'action:favorite': { metadata?: string };
  'action:unfavorite': { metadata?: string };
  // Avatar events
  'avatar:save-requested': { config: AvatarConfig };
  'avatar:config-loaded': { config: AvatarConfig };
  // Add more events as needed
}

export type EventName = keyof EventMap;
export type EventPayload<T extends EventName> = EventMap[T];

interface EventState {
  events: Partial<EventMap>;
  listeners: Partial<{
    [K in EventName]: Array<(payload: EventPayload<K>) => void>;
  }>;
}

export const useEventStore = defineStore('events', {
  state: (): EventState => ({
    events: {},
    listeners: {},
  }),

  actions: {
    // Emit an event with type safety
    emit<T extends EventName>(eventName: T, payload: EventPayload<T>) {
      this.events[eventName] = payload;

      // Trigger all listeners for this event
      const eventListeners = this.listeners[eventName];
      if (eventListeners) {
        eventListeners.forEach((callback) => {
          (callback as (payload: EventPayload<T>) => void)(payload);
        });
      }
    },

    // Subscribe to an event with type safety
    on<T extends EventName>(
      eventName: T,
      callback: (payload: EventPayload<T>) => void,
    ): () => void {
      if (!this.listeners[eventName]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.listeners as any)[eventName] = [] as Array<
          (payload: EventPayload<T>) => void
        >;
      }

      const listeners = this.listeners[eventName] as Array<
        (payload: EventPayload<T>) => void
      >;
      listeners.push(callback);

      // Return unsubscribe function
      return () => this.off(eventName, callback);
    },

    // Unsubscribe from an event
    off<T extends EventName>(
      eventName: T,
      callback: (payload: EventPayload<T>) => void,
    ) {
      const listeners = this.listeners[eventName] as
        | Array<(payload: EventPayload<T>) => void>
        | undefined;
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    },

    // Get the last emitted value for an event
    getLastEvent<T extends EventName>(
      eventName: T,
    ): EventPayload<T> | undefined {
      return this.events[eventName] as EventPayload<T> | undefined;
    },
  },
});
